// Admin API service
import api from '../../services/api';

// Types
export interface AdminStats {
  users: {
    total_users: string;
    new_users_week: string;
    new_users_month: string;
    active_users_week: string;
  };
  recipes: {
    total_recipes: string;
    flagged_recipes: string;
    stub_recipes: string;
  };
  ingredients: {
    total_ingredients: string;
  };
  mealPlans: {
    total_plans: string;
    active_plans: string;
    plans_created_week: string;
  };
  memberships: {
    active_memberships: string;
    auto_renew_count: string;
  };
}

export interface Recipe {
  recipe_id: number;
  name: string;
  description: string;
  instructions: string;
  nutrition_info: any;
  image_url: string | null;
  image_photographer?: string | null;
  image_photographer_url?: string | null;
  prep_time: number;
  cook_time: number | null;
  total_time: number | null;
  created_at: string;
  flagged_for_review: boolean;
  flag_reason: string | null;
  image_locked: boolean;
  usage_count: number;
  ingredient_count?: number;
  step_count?: number;
  ingredients?: RecipeIngredient[];
  steps?: RecipeStep[];
  tags?: RecipeTag[];
  usage?: { meal_count: number; plan_count: number };
}

export interface RecipeIngredient {
  recipe_ingredient_id: number;
  ingredient_id: number;
  quantity: number;
  unit: string;
  name: string;
  category: string;
}

export interface RecipeStep {
  step_id: number;
  step_number: number;
  description: string;
  header: string | null;
  image_url: string | null;
}

export interface RecipeTag {
  recipe_tag_id: number;
  tag_id: number;
  tag_name: string;
  type: string;
}

export interface Ingredient {
  ingredient_id: number;
  name: string;
  category: string;
  calories: number | null;
  protein: number | null;
  carbs: number | null;
  fats: number | null;
  sugar: number | null;
  fiber: number | null;
  serving_size: number | null;
  serving_size_unit: string | null;
  is_tracked: boolean;
  recipe_count?: number;
  synonyms?: { synonym_id: number; synonym: string }[];
  tags?: { ingredient_tag_id: number; tag_id: number; tag_name: string; type: string }[];
  usage?: { recipe_count: string; shopping_list_count: string; pantry_count: string };
  canDelete?: boolean;
}

export interface AdminUser {
  user_id: number;
  email: string;
  role: string;
  created_at: string;
  last_active_at: string | null;
  profile_complete: boolean;
  membership_status: string | null;
  membership_end_date: string | null;
  membership_name: string | null;
  auto_renew: boolean | null;
  funnel_step: string | null;
  stats?: {
    familyMemberCount: number;
    mealPlanCount: number;
  };
  membership?: any;
}

export interface PaginatedResponse<T> {
  success: boolean;
  total: number;
  page: number;
  totalPages: number;
  recipes?: T[];
  ingredients?: T[];
  users?: T[];
}

// Dashboard
export const getStats = async (): Promise<AdminStats> => {
  const response = await api.get('/admin/stats');
  return response.data.stats;
};

// Recipes
export const searchRecipes = async (params: {
  query?: string;
  page?: number;
  limit?: number;
  flagged?: boolean | null;
  hasIngredients?: boolean | null;
  noImage?: boolean | null;
  dalleOnly?: boolean | null;
}): Promise<PaginatedResponse<Recipe>> => {
  const response = await api.get('/admin/recipes', { params });
  return response.data;
};

export const createRecipe = async (data: {
  name: string;
  description?: string;
  instructions?: string;
  prep_time?: number;
  image_url?: string;
  base_servings?: number;
  chef_id?: number | null;
  ingredients?: { ingredient_id: number; quantity: number; unit: string }[];
  steps?: { step_number: number; description: string; header?: string }[];
}): Promise<Recipe> => {
  const response = await api.post('/admin/recipes', data);
  return response.data.recipe;
};

export const getRecipe = async (id: number): Promise<Recipe> => {
  const response = await api.get(`/admin/recipes/${id}`);
  return response.data.recipe;
};

export const updateRecipe = async (id: number, data: Partial<Recipe>): Promise<Recipe> => {
  const response = await api.put(`/admin/recipes/${id}`, data);
  return response.data.recipe;
};

export const deleteRecipe = async (id: number, reason?: string, hard = false): Promise<void> => {
  await api.delete(`/admin/recipes/${id}${hard ? '?hard=true' : ''}`, { data: { reason } });
};

export const flagRecipe = async (id: number, reason: string): Promise<Recipe> => {
  const response = await api.put(`/admin/recipes/${id}/flag`, { reason });
  return response.data.recipe;
};

export const unflagRecipe = async (id: number): Promise<Recipe> => {
  const response = await api.put(`/admin/recipes/${id}/unflag`);
  return response.data.recipe;
};

// Recipe Purge (flagged cleanup)
export interface PurgeResult {
  deleted: number;
  skipped: number;
  deleted_names: string[];
  skipped_names: string[];
}

export const purgeFlaggedRecipes = async (): Promise<PurgeResult> => {
  const response = await api.post('/admin/recipes/flagged/purge');
  return response.data;
};

// Recipe Tags
export const addRecipeTag = async (
  recipeId: number,
  tagName: string
): Promise<RecipeTag> => {
  const response = await api.post(`/admin/recipes/${recipeId}/tags`, { tag_name: tagName });
  return response.data.tag;
};

export const removeRecipeTag = async (recipeId: number, recipeTagId: number): Promise<void> => {
  await api.delete(`/admin/recipes/${recipeId}/tags/${recipeTagId}`);
};

export const getDietaryTags = async (): Promise<{ tag_id: number; tag_name: string; type: string }[]> => {
  const response = await api.get('/dietary-tags');
  return response.data;
};

// Recipe Ingredients
export const addRecipeIngredient = async (
  recipeId: number,
  data: { ingredient_id: number; quantity: number; unit: string }
): Promise<RecipeIngredient> => {
  const response = await api.post(`/admin/recipes/${recipeId}/ingredients`, data);
  return response.data.recipeIngredient;
};

export const updateRecipeIngredient = async (
  recipeId: number,
  riId: number,
  data: Partial<{ ingredient_id: number; quantity: number; unit: string }>
): Promise<RecipeIngredient> => {
  const response = await api.put(`/admin/recipes/${recipeId}/ingredients/${riId}`, data);
  return response.data.recipeIngredient;
};

export const removeRecipeIngredient = async (recipeId: number, riId: number): Promise<void> => {
  await api.delete(`/admin/recipes/${recipeId}/ingredients/${riId}`);
};

// Recipe Steps
export const addRecipeStep = async (
  recipeId: number,
  data: { step_number: number; description: string; header?: string }
): Promise<RecipeStep> => {
  const response = await api.post(`/admin/recipes/${recipeId}/steps`, data);
  return response.data.step;
};

export const updateRecipeStep = async (
  recipeId: number,
  stepId: number,
  data: Partial<RecipeStep>
): Promise<RecipeStep> => {
  const response = await api.put(`/admin/recipes/${recipeId}/steps/${stepId}`, data);
  return response.data.step;
};

export const deleteRecipeStep = async (recipeId: number, stepId: number): Promise<void> => {
  await api.delete(`/admin/recipes/${recipeId}/steps/${stepId}`);
};

// Ingredients
export const searchIngredients = async (params: {
  query?: string;
  page?: number;
  limit?: number;
  category?: string;
}): Promise<PaginatedResponse<Ingredient>> => {
  const response = await api.get('/admin/ingredients', { params });
  return response.data;
};

export const getIngredient = async (id: number): Promise<Ingredient> => {
  const response = await api.get(`/admin/ingredients/${id}`);
  return response.data.ingredient;
};

export const createIngredient = async (data: Partial<Ingredient>): Promise<Ingredient> => {
  const response = await api.post('/admin/ingredients', data);
  return response.data.ingredient;
};

export const updateIngredient = async (id: number, data: Partial<Ingredient>): Promise<Ingredient> => {
  const response = await api.put(`/admin/ingredients/${id}`, data);
  return response.data.ingredient;
};

export const deleteIngredient = async (id: number): Promise<void> => {
  await api.delete(`/admin/ingredients/${id}`);
};

export const addSynonym = async (ingredientId: number, synonym: string): Promise<void> => {
  await api.post(`/admin/ingredients/${ingredientId}/synonyms`, { synonym });
};

export const removeSynonym = async (ingredientId: number, synonymId: number): Promise<void> => {
  await api.delete(`/admin/ingredients/${ingredientId}/synonyms/${synonymId}`);
};

// Users
export const searchUsers = async (params: {
  query?: string;
  status?: string;
  active_within_days?: number;
  sort_by?: 'email' | 'created_at' | 'last_active_at' | 'membership_status';
  sort_order?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}): Promise<PaginatedResponse<AdminUser>> => {
  const response = await api.get('/admin/users', { params });
  return response.data;
};

export const getUser = async (id: number): Promise<AdminUser> => {
  const response = await api.get(`/admin/users/${id}`);
  return response.data.user;
};

export const updateUserMembership = async (
  userId: number,
  data: { membership_id?: number; status?: string; end_date?: string; auto_renew?: boolean }
): Promise<void> => {
  await api.put(`/admin/users/${userId}/membership`, data);
};

export const updateUserRole = async (userId: number, role: string): Promise<void> => {
  await api.put(`/admin/users/${userId}/role`, { role });
};

// Admin user detail — family members + meal plans
export interface AdminFamilyMemberTag {
  member_id: number;
  tag_id: number;
  tag_name: string;
  type: 'restriction' | 'medical' | 'allergy' | 'goal' | 'cuisine' | 'user_generated';
  severity: string | null;
}

export interface AdminFamilyMember {
  member_id: number;
  name: string;
  is_child: boolean | null;
  meal_schedule: Record<string, string[]> | null;
  dietary_tags: AdminFamilyMemberTag[];
  ingredient_dislikes: string[];
}

export interface AdminMealPlanSummary {
  plan_id: number;
  week_start_date: string;
  status: string | null;
  meal_count: string; // pg returns COUNT as string
}

export const getUserFamilyMembers = async (userId: number): Promise<AdminFamilyMember[]> => {
  const response = await api.get(`/admin/users/${userId}/family-members`);
  return response.data.members || [];
};

export const getUserMealPlans = async (userId: number): Promise<AdminMealPlanSummary[]> => {
  const response = await api.get(`/admin/users/${userId}/meal-plans`);
  return response.data.plans || [];
};

export const adminDeleteMealPlan = async (planId: number): Promise<void> => {
  await api.delete(`/admin/meal-plans/${planId}`);
};

// Duplicate Recipe types
export interface DuplicateRecipe {
  recipe_id: number;
  name: string;
  name_normalized: string;
  usage_count: number;
  created_at: string;
  ingredient_count: number;
  step_count: number;
  meal_count: number;
  in_active_plan: boolean;
  is_expanded: boolean;
}

export interface DuplicateGroup {
  normalized_name: string;
  count: number;
  in_active_plans: number;
  recommended_keep: number;
  recipes: DuplicateRecipe[];
}

export interface DuplicatesResponse {
  success: boolean;
  total_groups: number;
  total_duplicates: number;
  groups: DuplicateGroup[];
}

export interface CleanupResult {
  success: boolean;
  kept_recipe: { recipe_id: number; name: string };
  deleted_recipes: number;
  deleted_ids: number[];
  skipped_recipes: number;
  skipped_names: string[];
}

// Duplicate Recipe API calls
export const getDuplicateRecipes = async (): Promise<DuplicatesResponse> => {
  const response = await api.get('/admin/recipes/duplicates');
  return response.data;
};

export const cleanupDuplicates = async (
  keepRecipeId: number,
  deleteRecipeIds: number[]
): Promise<CleanupResult> => {
  const response = await api.post('/admin/recipes/duplicates/cleanup', {
    keepRecipeId,
    deleteRecipeIds,
  });
  return response.data;
};

// Enrichment types
export interface EnrichmentStats {
  total: number;
  with_ingredients: number;
  with_steps: number;
  with_tags: number;
  with_nutrition: number;
  standard_servings: number;
  stubs: number;
  missing_steps: number;
  missing_tags: number;
  missing_nutrition: number;
  non_standard_servings: number;
}

export interface IncompleteRecipe {
  recipe_id: number;
  name: string;
  base_servings: number;
  has_ingredients: boolean;
  has_steps: boolean;
  has_tags: boolean;
  has_nutrition: boolean;
}

export interface BatchEnrichmentResult {
  processed: number;
  succeeded: number;
  failed: number;
  skipped: number;
  errors: { recipe_id: number; name: string; error: string }[];
}

export interface StandardizeResult {
  processed: number;
  standardized: number;
  failed: number;
  errors: { recipe_id: number; error: string }[];
}

// Enrichment API calls
export const getEnrichmentStats = async (): Promise<EnrichmentStats> => {
  const response = await api.get('/admin/recipes/enrichment/stats');
  return response.data.stats;
};

export const getIncompleteRecipes = async (limit = 100, offset = 0): Promise<IncompleteRecipe[]> => {
  const response = await api.get('/admin/recipes/enrichment/incomplete', { params: { limit, offset } });
  return response.data.recipes;
};

export const batchEnrichRecipes = async (options?: {
  limit?: number;
  stubsOnly?: boolean;
  recipeIds?: number[];
}): Promise<{ jobId: string }> => {
  const response = await api.post('/admin/recipes/enrichment/batch', options || {});
  return { jobId: response.data.jobId };
};

export const getEnrichmentStatus = async (jobId: string): Promise<{
  status: 'running' | 'done' | 'error';
  total?: number;
  results: BatchEnrichmentResult;
  error?: string;
}> => {
  const response = await api.get(`/admin/recipes/enrichment/status/${jobId}`);
  return response.data;
};

export const enrichSingleRecipe = async (recipeId: number): Promise<{ recipe_id: number; status: string; skipped: boolean }> => {
  const response = await api.post(`/admin/recipes/${recipeId}/enrich`);
  return response.data;
};

export const standardizeAllServings = async (): Promise<StandardizeResult> => {
  const response = await api.post('/admin/recipes/enrichment/standardize');
  return response.data.results;
};

// Single-recipe image regeneration (uses Flux+QA pipeline).
// Batch image generation lives at /admin/image-backfill — see the BackfillJob types below.
export const regenerateRecipeImage = async (
  recipeId: number,
  options?: { model?: string; updateResultId?: number }
): Promise<{ image_url: string; attempts?: number; qa_passed?: boolean; qa_issues?: string | null }> => {
  const body: Record<string, any> = {};
  if (options?.model) body.model = options.model;
  if (options?.updateResultId) body.update_result_id = options.updateResultId;
  const response = await api.post(`/admin/recipes/${recipeId}/regenerate-image`, body);
  return response.data;
};

// ============================================
// RECIPE SEEDING TYPES & API CALLS
// ============================================

export interface LibraryStat {
  meal_type: string;
  total: number;
  complete: number;
}

export interface SeedingPreview {
  name: string;
  description: string;
  total_time: number;
  dietary_tags: string[];
  cuisine?: string | null;
}

export interface SeedingResult {
  created: number;
  failed: number;
  skipped: number;
  recipes: { recipe_id: number; name: string }[];
  errors: { name: string; error: string }[];
}

export const getLibraryStats = async (): Promise<LibraryStat[]> => {
  const response = await api.get('/admin/recipes/seeding/stats');
  return response.data.stats;
};

export const generateSeedRecipes = async (params: {
  meal_type: string;
  dietary_tags?: string[];
  count?: number;
  kid_friendly?: boolean;
  cuisine?: string;
  required_ingredient?: string;
  max_total_time?: number;
  quick_easy?: boolean;
  is_modular?: boolean;
}): Promise<SeedingPreview[]> => {
  const response = await api.post('/admin/recipes/seeding/generate', params);
  return response.data.recipes;
};

export const batchSeedRecipes = async (
  recipes: SeedingPreview[],
  meal_type: string,
  is_modular?: boolean
): Promise<{ jobId: string }> => {
  const response = await api.post('/admin/recipes/seeding/batch', { recipes, meal_type, is_modular });
  return { jobId: response.data.jobId };
};

export const getSeedingStatus = async (jobId: string): Promise<{
  status: 'running' | 'done' | 'error';
  total?: number;
  progress?: number;
  results: SeedingResult;
  error?: string;
}> => {
  const response = await api.get(`/admin/recipes/seeding/status/${jobId}`);
  return response.data;
};

// Fuzzy Duplicate API calls
export interface FuzzyDuplicateGroup extends DuplicateGroup {
  similarity_score: string;
}

export interface FuzzyDuplicatesResponse {
  success: boolean;
  total_groups: number;
  total_duplicates: number;
  groups: FuzzyDuplicateGroup[];
}

export const getFuzzyDuplicates = async (threshold: number = 0.5): Promise<FuzzyDuplicatesResponse> => {
  const response = await api.get('/admin/recipes/duplicates/fuzzy', { params: { threshold } });
  return response.data;
};

// Demo Meals types
export interface DemoMeal {
  demo_meal_id: number;
  day_of_week: number;
  meal_type: 'breakfast' | 'lunch' | 'dinner';
  recipe_id: number;
  display_order: number;
  created_at: string;
  updated_at: string;
  recipe_name: string;
  recipe_description: string;
  image_url: string | null;
  prep_time: number;
  nutrition_info: any;
}

export interface DemoMealsResponse {
  success: boolean;
  demoMeals: DemoMeal[];
  dayLabels: string[];
  mealTypes: string[];
}

// Demo Meals API calls
export const getDemoMeals = async (): Promise<DemoMealsResponse> => {
  const response = await api.get('/admin/demo-meals');
  return response.data;
};

export const setDemoMeal = async (
  day: number,
  mealType: string,
  recipeId: number
): Promise<{ success: boolean; demoMeal: DemoMeal }> => {
  const response = await api.put(`/admin/demo-meals/${day}/${mealType}`, { recipeId });
  return response.data;
};

export const removeDemoMeal = async (
  day: number,
  mealType: string
): Promise<{ success: boolean; message: string }> => {
  const response = await api.delete(`/admin/demo-meals/${day}/${mealType}`);
  return response.data;
};

// Push Notification types
export interface PushToken {
  token_id: number;
  user_id: number;
  email: string;
  expo_push_token: string;
  platform: 'ios' | 'android';
  is_active: boolean;
  app_version: string | null;
  created_at: string;
  last_used_at: string;
}

export interface NotificationLog {
  log_id: number;
  user_id: number;
  email?: string;
  notification_type: string;
  title: string;
  body: string;
  status: 'sent' | 'failed';
  error_message: string | null;
  sent_at: string;
}

// Push Notification API calls
export const getPushTokens = async (): Promise<{ success: boolean; count: number; tokens: PushToken[] }> => {
  const response = await api.get('/admin/notifications/tokens');
  return response.data;
};

export const getNotificationLogs = async (): Promise<{ success: boolean; logs: NotificationLog[] }> => {
  const response = await api.get('/admin/notifications/logs');
  return response.data;
};

export const sendTestNotification = async (
  userId: number,
  title: string,
  body: string,
  data?: Record<string, any>
): Promise<{ success: boolean; message: string; result: { sent: number; failed: number } }> => {
  const response = await api.post('/admin/notifications/test', { userId, title, body, data });
  return response.data;
};

// Creator types
export interface Creator {
  creator_id: number;
  email: string;
  name: string;
  bio: string | null;
  photo_url: string | null;
  website_url: string | null;
  social_links: Record<string, string> | null;
  platforms: string[] | null;
  follower_count: number | null;
  niche: string | null;
  status: 'pending' | 'approved' | 'active' | 'suspended' | 'rejected';
  approved_at: string | null;
  user_id: number | null;
  notes: string | null;
  rejection_reason: string | null;
  promo_code: string | null;
  code_uses: number | null;
  total_referrals: number | null;
  active_referrals: number | null;
  created_at: string;
  updated_at: string;
}

export interface CreateCreatorData {
  email: string;
  name: string;
  bio?: string;
  website_url?: string;
  social_links?: {
    instagram?: string;
    tiktok?: string;
    facebook?: string;
    twitter?: string;
    youtube?: string;
  };
  platforms?: string[];
  follower_count?: number;
  niche?: string;
  notes?: string;
}

// Creator API calls
export const getCreators = async (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; creators: Creator[] }> => {
  const response = await api.get('/creators', { params });
  return response.data;
};

export const getCreator = async (id: number): Promise<{ success: boolean; creator: Creator }> => {
  const response = await api.get(`/creators/${id}`);
  return response.data;
};

export const createCreator = async (data: CreateCreatorData): Promise<{ success: boolean; message: string; creator: Creator }> => {
  const response = await api.post('/creators', data);
  return response.data;
};

export const saveCreatorAsPending = async (data: CreateCreatorData): Promise<{ success: boolean; message: string; creator_id: number }> => {
  const response = await api.post('/creators/apply', data);
  return response.data;
};

export const approveCreator = async (id: number): Promise<{ success: boolean; message: string; creator: Creator }> => {
  const response = await api.post(`/creators/${id}/approve`);
  return response.data;
};

export const rejectCreator = async (id: number, reason: string): Promise<{ success: boolean; message: string; creator: Creator }> => {
  const response = await api.post(`/creators/${id}/reject`, { reason });
  return response.data;
};

export const suspendCreator = async (id: number, reason: string, terminationType: 'for_cause' | 'without_cause'): Promise<{ success: boolean; message: string; creator: Creator }> => {
  const response = await api.post(`/creators/${id}/suspend`, { reason, termination_type: terminationType });
  return response.data;
};

export const resendCreatorWelcome = async (id: number): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/creators/${id}/resend-welcome`);
  return response.data;
};

// Admin Creator Dashboard types
export interface AdminCreatorDashboard {
  creator: {
    creator_id: number;
    name: string;
    email: string;
    status: string;
    promo_code: string;
    code_uses: number;
  };
  membership: {
    status: string;
    start_date: string;
    end_date: string;
  } | null;
  referrals: {
    total: number;
    trialing: number;
    active: number;
    cancelled: number;
    churned: number;
    new_this_month: number;
  };
  revenue: {
    total_revenue: number;
    total_commission: number;
    total_earned: number;
    pending_payout: number;
    total_paid: number;
  };
}

export interface CreatorPayout {
  payout_id: number;
  creator_id: number;
  amount: number;
  period_start: string;
  period_end: string;
  payment_method: string;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  payment_reference: string | null;
  processed_by: number | null;
  created_at: string;
  completed_at: string | null;
}

export interface CreatorReferral {
  referral_id: number;
  creator_id: number;
  user_id: number;
  user_email: string;
  user_created_at: string;
  signup_date: string;
  subscription_status: 'trialing' | 'active' | 'cancelled' | 'churned';
  first_paid_date: string | null;
  lifetime_revenue: number;
  lifetime_commission: number;
}

// Admin Creator detail API calls
export const getAdminCreatorDashboard = async (id: number): Promise<AdminCreatorDashboard> => {
  const response = await api.get(`/creators/${id}/dashboard`);
  return response.data.dashboard;
};

export const getAdminCreatorReferrals = async (
  id: number,
  params?: { status?: string; limit?: number; offset?: number }
): Promise<CreatorReferral[]> => {
  const response = await api.get(`/creators/${id}/referrals`, { params });
  return response.data.referrals;
};

export const getAdminCreatorPayouts = async (
  id: number,
  params?: { limit?: number; offset?: number }
): Promise<CreatorPayout[]> => {
  const response = await api.get(`/creators/${id}/payouts`, { params });
  return response.data.payouts;
};

export const processCreatorPayout = async (
  payoutId: number,
  paymentReference: string
): Promise<{ success: boolean; message: string; payout: CreatorPayout }> => {
  const response = await api.post(`/creators/payouts/${payoutId}/process`, {
    payment_reference: paymentReference,
  });
  return response.data;
};

export const extendCreatorMembership = async (
  creatorId: number,
  months: number
): Promise<{ success: boolean; message: string }> => {
  const response = await api.post(`/creators/${creatorId}/extend-membership`, { months });
  return response.data;
};

// ============================================
// CHEF TYPES & API CALLS
// ============================================

export interface Chef {
  chef_id: number;
  email: string;
  name: string;
  bio: string | null;
  photo_url: string | null;
  website_url: string | null;
  social_links: Record<string, string> | null;
  cultural_background: string | null;
  specialties: string | null;
  credentials: string | null;
  slug: string | null;
  status: 'pending' | 'approved' | 'active' | 'suspended' | 'rejected';
  tier: string | null;
  recipe_rate: number | null;
  contract_start_date: string | null;
  contract_end_date: string | null;
  approved_at: string | null;
  approved_by: number | null;
  user_id: number | null;
  is_active: boolean;
  notes: string | null;
  rejection_reason: string | null;
  promo_code: string | null;
  code_uses: number | null;
  recipe_count: number | null;
  created_at: string;
  updated_at: string;
}

export interface AdminChefDashboard {
  chef: {
    chef_id: number;
    name: string;
    email: string;
    slug: string;
    status: string;
    tier: string;
    recipe_rate: number;
    promo_code: string;
    code_uses: number;
    contract_end_date: string;
  };
  recipes: {
    total: number;
    curated: number;
  };
  usage: {
    total_meal_appearances: number;
  };
}

export interface ChefPayment {
  payment_id: number;
  chef_id: number;
  period_year: number;
  recipe_count: number;
  rate_per_recipe: number;
  total_amount: number;
  status: 'pending' | 'paid';
  paid_date: string | null;
  payment_reference: string | null;
  processed_by: number | null;
}

export interface ChefRecipe {
  recipe_id: number;
  name: string;
  description: string;
  prep_time: number;
  cook_time: number | null;
  image_url: string | null;
  cuisine: string | null;
  servings: number | null;
  source_type: string;
  dietary_tags: string[] | null;
  created_at: string;
}

export interface CreateChefData {
  email: string;
  name: string;
  bio?: string;
  website_url?: string;
  social_links?: {
    instagram?: string;
    tiktok?: string;
    facebook?: string;
    twitter?: string;
  };
  cultural_background?: string;
  specialties?: string;
  credentials?: string;
  application_notes?: string;
  tier?: string;
  recipe_rate?: number;
}

// Chef API calls
export const createChef = async (data: CreateChefData): Promise<{ success: boolean; message: string; chef: Chef }> => {
  const response = await api.post('/chefs', data);
  return response.data;
};

export const saveChefAsPending = async (data: CreateChefData): Promise<{ success: boolean; message: string; chef_id: number }> => {
  const response = await api.post('/chefs/apply', data);
  return response.data;
};

export const getChefs = async (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<{ success: boolean; chefs: Chef[] }> => {
  const response = await api.get('/chefs', { params });
  return response.data;
};

export const getChef = async (id: number): Promise<{ success: boolean; chef: Chef }> => {
  const response = await api.get(`/chefs/${id}`);
  return response.data;
};

export const getAdminChefDashboard = async (id: number): Promise<AdminChefDashboard> => {
  const response = await api.get(`/chefs/${id}/dashboard`);
  return response.data.dashboard;
};

export const getAdminChefRecipes = async (
  id: number,
  params?: { limit?: number; offset?: number }
): Promise<ChefRecipe[]> => {
  const response = await api.get(`/chefs/${id}/recipes`, { params });
  return response.data.recipes;
};

export const approveChef = async (
  id: number,
  options?: { tier?: string; recipe_rate?: number }
): Promise<{ success: boolean; message: string; chef: Chef }> => {
  const response = await api.post(`/chefs/${id}/approve`, options);
  return response.data;
};

export const rejectChef = async (
  id: number,
  reason: string
): Promise<{ success: boolean; message: string; chef: Chef }> => {
  const response = await api.post(`/chefs/${id}/reject`, { reason });
  return response.data;
};

export const suspendChef = async (
  id: number,
  reason: string
): Promise<{ success: boolean; message: string; chef: Chef }> => {
  const response = await api.post(`/chefs/${id}/suspend`, { reason });
  return response.data;
};

export const recordChefPayment = async (
  id: number,
  year: number,
  paymentReference: string
): Promise<{ success: boolean; message: string; payment: ChefPayment }> => {
  const response = await api.post(`/chefs/${id}/pay`, {
    year,
    payment_reference: paymentReference,
  });
  return response.data;
};

export const getActiveChefs = async (): Promise<Chef[]> => {
  const response = await api.get('/chefs/active');
  return response.data.chefs;
};

// ============================================
// PROMO CODE TYPES & API CALLS
// ============================================

export interface PromoCode {
  code_id: number;
  code: string;
  code_type: 'creator' | 'chef' | 'marketing' | 'beta';
  creator_id: number | null;
  chef_id: number | null;
  trial_days: number;
  discount_percent: number | null;
  max_uses: number | null;
  uses_count: number;
  is_active: boolean;
  valid_from: string;
  valid_until: string | null;
  created_at: string;
  creator_name: string | null;
  chef_name: string | null;
}

export interface PromoCodeStats {
  code_id: number;
  code: string;
  code_type: string;
  total_referrals: number;
  active_referrals: number;
  trialing_referrals: number;
  churned_referrals: number;
  total_revenue: number;
  total_commission: number;
}

export interface CreatePromoCodeData {
  code: string;
  code_type: 'creator' | 'chef' | 'marketing' | 'beta';
  trial_days?: number;
  discount_percent?: number | null;
  max_uses?: number | null;
  valid_until?: string | null;
}

export const getPromoCodes = async (params?: {
  code_type?: string;
  is_active?: boolean;
  limit?: number;
  offset?: number;
}): Promise<PromoCode[]> => {
  const response = await api.get('/promo-codes', { params });
  return response.data.codes;
};

export const createPromoCode = async (data: CreatePromoCodeData): Promise<PromoCode> => {
  const response = await api.post('/promo-codes', data);
  return response.data.code;
};

export const getPromoCodeStats = async (id: number): Promise<PromoCodeStats> => {
  const response = await api.get(`/promo-codes/${id}/stats`);
  return response.data.stats;
};

export const deactivatePromoCode = async (id: number): Promise<PromoCode> => {
  const response = await api.put(`/promo-codes/${id}/deactivate`);
  return response.data.code;
};

export const reactivatePromoCode = async (id: number): Promise<PromoCode> => {
  const response = await api.put(`/promo-codes/${id}/reactivate`);
  return response.data.code;
};

// ============================================
// PAYOUT QUEUE TYPES & API CALLS
// ============================================

export interface PayoutQueueItem {
  payout_id: number;
  creator_id: number;
  amount: number;
  currency: string;
  period_start: string;
  period_end: string;
  payment_method: string;
  payment_reference: string | null;
  status: 'pending' | 'processing' | 'completed' | 'failed';
  requested_at: string;
  processed_at: string | null;
  completed_at: string | null;
  processed_by: number | null;
  notes: string | null;
  created_at: string;
  creator_name: string;
  creator_email: string;
  creator_payment_method: string | null;
  creator_payment_details: string | null;
}

export const getAllPayouts = async (params?: {
  status?: string;
  limit?: number;
  offset?: number;
}): Promise<PayoutQueueItem[]> => {
  const response = await api.get('/creators/payouts/pending', { params });
  return response.data.payouts;
};

// Churn Surveys
export interface ChurnSurvey {
  id: number;
  user_id: number;
  reason: string;
  feedback: string | null;
  created_at: string;
  email: string;
}

export interface ChurnSurveyStats {
  reason: string;
  count: number;
}

export const getChurnSurveys = async (): Promise<{
  surveys: ChurnSurvey[];
  total: number;
  stats: ChurnSurveyStats[];
}> => {
  const response = await api.get('/admin/churn-surveys');
  return response.data;
};

// ============================================
// JOBS & SYNC (MailerLite, cron triggers)
// ============================================

export interface SegmentCounts {
  [segment: string]: number;
}

export const getMailerliteStatus = async (): Promise<{
  configured: boolean;
  segments: SegmentCounts;
}> => {
  const response = await api.get('/admin/mailerlite/status');
  return { configured: response.data.configured, segments: response.data.segments };
};

export const syncAllMailerlite = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/admin/mailerlite/sync-all');
  return response.data;
};

export const resetAndResyncMailerlite = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/admin/mailerlite/reset-and-resync');
  return response.data;
};

export const runTrialReminder = async (): Promise<any> => {
  const response = await api.post('/admin/trial-reminder/run');
  return response.data;
};

export const previewTrialReminder = async (): Promise<{
  count: number;
  users: Array<{ user_id: number; email: string; name: string; trial_end_date: string }>;
}> => {
  const response = await api.get('/admin/trial-reminder/preview');
  return response.data;
};

export const runOnboardingReminder = async (): Promise<any> => {
  const response = await api.post('/admin/onboarding-reminder/run');
  return response.data;
};

export const previewOnboardingReminder = async (): Promise<{
  count: number;
  users: Array<{ user_id: number; email: string; first_name: string }>;
}> => {
  const response = await api.get('/admin/onboarding-reminder/preview');
  return response.data;
};

export const runSegmentSync = async (): Promise<{ success: boolean; message: string }> => {
  const response = await api.post('/admin/segment-sync/run');
  return response.data;
};

export const previewExpireSweep = async (): Promise<{
  count: number;
  users: Array<{ user_id: number; email: string; first_name: string; end_date: string }>;
}> => {
  const response = await api.get('/admin/expire-sweep/preview');
  return response.data;
};

export const runExpireSweep = async (): Promise<{
  success: boolean;
  message: string;
  count?: number;
  user_ids?: number[];
}> => {
  const response = await api.post('/admin/expire-sweep/run');
  return response.data;
};

// ============================================
// MAILERLITE EXPIRE-DEAD
// ============================================
// Permanent-churn expiry: flags users as marketing_opt_in=false AND deletes
// them from MailerLite. Keeps the subscriber count from drifting past the
// free-tier limit as users age out of engagement. Runs nightly at 04:00 UTC;
// preview + run endpoints also available for manual trigger.

export interface MailerliteExpireDeadSummary {
  success: boolean;
  candidates: number;
  expiredCount: number;
  mailerliteErrors: number;
  dbErrors: number;
  sample: Array<{
    user_id: number;
    email: string;
    status: string | null;
    days_inactive: number | null;
  }>;
  dryRun: boolean;
  message?: string;
}

export const previewMailerliteExpireDead = async (): Promise<MailerliteExpireDeadSummary> => {
  const response = await api.get('/admin/mailerlite-expire-dead/preview');
  return response.data;
};

export const runMailerliteExpireDead = async (): Promise<MailerliteExpireDeadSummary> => {
  const response = await api.post('/admin/mailerlite-expire-dead/run');
  return response.data;
};

// ============================================
// DIETARY TAG BACKFILL
// ============================================
// Deterministic add-only backfill of dietary/allergen/nutrition tags for
// every complete recipe. Preview returns what would change; run applies it.
// Safe to re-run — never removes tags, dedups against existing.

export interface DietaryBackfillSummary {
  success: boolean;
  recipesProcessed: number;
  pendingCount: number;
  gainedByTag: Record<string, number>;
  missingTagNames: string[];
  sampleRecipes: Array<{ recipe_id: number; name: string; added: string[] }>;
  // Present on the /run response only
  inserted?: number;
  message?: string;
}

export const previewDietaryTagBackfill = async (): Promise<DietaryBackfillSummary> => {
  const response = await api.get('/admin/dietary-tags/backfill/preview');
  return response.data;
};

export const runDietaryTagBackfill = async (): Promise<DietaryBackfillSummary> => {
  const response = await api.post('/admin/dietary-tags/backfill/run');
  return response.data;
};

// ============================================
// IMAGE QUALITY TEST (Flux Pro 1.1 vs DALL-E)
// ============================================

export interface ImageTest {
  test_id: number;
  recipe_id: number;
  recipe_name: string;
  model: string;
  prompt: string;
  current_image_url: string | null;
  new_image_url: string;
  rating: 'new_better' | 'current_better' | 'both_bad' | null;
  notes: string | null;
  created_at: string;
  photorealism_score: number | null;
  accuracy_score: number | null;
  appetizing_score: number | null;
  qa_passes: boolean | null;
  qa_attempts: number | null;
  qa_issues: string | null;
}

export interface FluxModel {
  key: string;
  label: string;
  endpoint: string;
  costPerImage: number;
  note: string;
}

export const listFluxModels = async (): Promise<{ models: FluxModel[]; default: string }> => {
  const response = await api.get('/admin/image-test/models');
  return { models: response.data.models, default: response.data.default };
};

export const pickRandomImageTestRecipes = async (count: number = 10): Promise<{ recipe_id: number; name: string }[]> => {
  const response = await api.get('/admin/image-test/random', { params: { count } });
  return response.data.recipes;
};

export const generateImageTest = async (recipeId: number, model?: string): Promise<ImageTest> => {
  const response = await api.post('/admin/image-test/generate', { recipe_id: recipeId, model });
  return response.data.test;
};

export const listImageTests = async (): Promise<ImageTest[]> => {
  const response = await api.get('/admin/image-test/list');
  return response.data.tests;
};

export const rateImageTest = async (testId: number, rating: ImageTest['rating'], notes?: string): Promise<ImageTest> => {
  const response = await api.post(`/admin/image-test/${testId}/rate`, { rating, notes });
  return response.data.test;
};

export const deleteImageTest = async (testId: number): Promise<void> => {
  await api.delete(`/admin/image-test/${testId}`);
};

// ============================================
// IMAGE BACKFILL (production: Flux + QA pipeline)
// ============================================

export type BackfillMode = 'all' | 'missing' | 'legacy_dalle' | 'specific';
export type BackfillJobStatus = 'pending' | 'running' | 'paused' | 'completed' | 'cancelled' | 'failed';
export type BackfillResultStatus = 'success' | 'failed_qa' | 'error';

export interface BackfillJob {
  job_id: number;
  status: BackfillJobStatus;
  total_recipes: number;
  processed_count: number;
  passed_count: number;
  failed_count: number;
  config: any;
  started_at: string | null;
  completed_at: string | null;
  cancel_requested: boolean;
  error_message: string | null;
  created_at: string;
}

export interface BackfillResult {
  result_id: number;
  job_id: number;
  recipe_id: number;
  recipe_name: string;
  status: BackfillResultStatus;
  cdn_url: string | null;
  current_image_url: string | null;
  attempts: number | null;
  photorealism_score: number | null;
  accuracy_score: number | null;
  appetizing_score: number | null;
  qa_issues: string | null;
  error_message: string | null;
  created_at: string;
}

export const previewBackfill = async (params: {
  mode: BackfillMode;
  recipe_ids?: string;
  limit?: number;
}): Promise<{ count: number; recipes: { recipe_id: number; name: string; image_url: string | null }[] }> => {
  const response = await api.get('/admin/image-backfill/preview', { params });
  return { count: response.data.count, recipes: response.data.recipes };
};

export const startBackfill = async (body: {
  mode: BackfillMode;
  recipe_ids?: number[];
  limit?: number;
}): Promise<{ job_id: number; total: number }> => {
  const response = await api.post('/admin/image-backfill/start', body);
  return { job_id: response.data.job_id, total: response.data.total };
};

export const listBackfillJobs = async (): Promise<BackfillJob[]> => {
  const response = await api.get('/admin/image-backfill/jobs');
  return response.data.jobs;
};

export const getBackfillJob = async (jobId: number, resultStatus?: BackfillResultStatus): Promise<{ job: BackfillJob; results: BackfillResult[] }> => {
  const response = await api.get(`/admin/image-backfill/jobs/${jobId}`, {
    params: resultStatus ? { result_status: resultStatus } : {},
  });
  return { job: response.data.job, results: response.data.results };
};

export const cancelBackfillJob = async (jobId: number): Promise<void> => {
  await api.post(`/admin/image-backfill/jobs/${jobId}/cancel`);
};
